/**
 * AppIcon – EPPREM 2.0 Healthcare Design System Icon Wrapper
 *
 * Standardizes icon sizes, inherits theme colors, supports optional tooltip
 * and accessibility labels. Use lucide-react ONLY.
 *
 * Sizes (Section 24.2 — LOCKED to 12–14px range):
 *   compact → 12px (dense metadata, badge icons)
 *   button  → 14px (all standard usage — default)
 *   sm, default, nav, lg, xl → all capped at 14px (legacy aliases)
 *
 * Status → Icon → Variant mapping (Section 24.4):
 *   Active → CheckCircle → success
 *   Inactive → CircleSlash → muted
 *   Suspended → PauseCircle → warning
 *   Draft → FileEdit → muted
 *   Published → BadgeCheck → primary
 *   Archived → Archive → muted
 *   Rejected → XCircle → error
 *   Pending → Clock → warning
 *   Completed → Check → success
 */

import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  CheckCircle,
  CircleSlash,
  PauseCircle,
  FileEdit,
  BadgeCheck,
  Archive,
  XCircle,
  Clock,
  Check,
  Eye,
  Pencil,
  Trash2,
  MoreHorizontal,
  Plus,
  Search,
  Filter,
  Download,
  Upload,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  X,
  Info,
  AlertTriangle,
  Settings,
  User,
  Users,
  Building2,
  FileText,
  Workflow,
  Bell,
  Shield,
  LogOut,
  Home,
  BarChart2,
  Lock,
  Unlock,
  Copy,
  ExternalLink,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

export type AppIconSize = 'sm' | 'default' | 'nav' | 'lg' | 'xl' | 'compact' | 'button';

export type AppIconVariant =
  | 'default'
  | 'primary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'muted'
  | 'inherit';

export interface AppIconProps {
  /** The Lucide icon component or an alias key from Icons map */
  icon?: LucideIcon;
  /** @deprecated Use `icon` instead. Kept for backward compat. */
  Icon?: LucideIcon;
  variant?: AppIconVariant;
  size?: AppIconSize;
  /** Accessibility label (renders aria-label + title) */
  label?: string;
  className?: string;
}

// ─── Size & Variant maps ──────────────────────────────────────────────────────

// Section 24.2: icon sizes are LOCKED to 12px (compact) and 14px (button).
// All legacy sizes (sm, default, nav, lg, xl) are remapped to 14px.
const SIZE_CLASSES: Record<AppIconSize, string> = {
  compact: 'w-3 h-3',     // 12px — use for dense metadata, badge icons
  button:  'w-3.5 h-3.5', // 14px — standard governed size (all other contexts)
  // Legacy aliases — all capped at 14px per Section 24.2
  sm:      'w-3.5 h-3.5', // was 16px → 14px
  default: 'w-3.5 h-3.5', // was 18px → 14px
  nav:     'w-3.5 h-3.5', // was 20px → 14px
  lg:      'w-3.5 h-3.5', // was 24px → 14px
  xl:      'w-3.5 h-3.5', // was 32px → 14px
};

const VARIANT_CLASSES: Record<AppIconVariant, string> = {
  default: 'text-muted-foreground',
  muted:   'text-muted-foreground',
  primary: 'text-primary',
  success: 'text-success',
  warning: 'text-warning',
  error:   'text-destructive',
  info:    'text-info',
  inherit: 'text-current',
};

// ─── AppIcon ──────────────────────────────────────────────────────────────────

/**
 * Standardized icon wrapper. Supports `icon` (new) and `Icon` (legacy) props.
 *
 * @example
 * <AppIcon icon={Edit} label="Edit patient" size="sm" />
 * <AppIcon Icon={CheckCircle} variant="success" />
 */
export function AppIcon({ icon, Icon: LegacyIcon, variant = 'default', size = 'default', label, className }: AppIconProps) {
  const IconComponent = icon || LegacyIcon;
  if (!IconComponent) return null;

  return (
    <IconComponent
      className={cn(
        SIZE_CLASSES[size],
        VARIANT_CLASSES[variant],
        'flex-shrink-0',
        className,
      )}
      aria-label={label}
      aria-hidden={!label}
      {...(label ? { title: label, role: 'img' } : {})}
    />
  );
}

// ─── Status mapping (Section 24.4) ───────────────────────────────────────────

type StatusKey =
  | 'active'   | 'ACTIVE'
  | 'inactive' | 'INACTIVE'
  | 'suspended'| 'SUSPENDED'
  | 'draft'    | 'DRAFT'
  | 'published'| 'PUBLISHED'
  | 'archived' | 'ARCHIVED'
  | 'rejected' | 'REJECTED'
  | 'pending'  | 'PENDING'
  | 'completed'| 'COMPLETED'
  | string;

interface StatusConfig {
  Icon: LucideIcon;
  variant: AppIconVariant;
  label: string; // Sentence case per badge standard
}

const STATUS_MAP: Record<string, StatusConfig> = {
  active:    { Icon: CheckCircle,  variant: 'success', label: 'Active' },
  inactive:  { Icon: CircleSlash,  variant: 'muted',   label: 'Inactive' },
  suspended: { Icon: PauseCircle,  variant: 'warning', label: 'Suspended' },
  draft:     { Icon: FileEdit,     variant: 'muted',   label: 'Draft' },
  published: { Icon: BadgeCheck,   variant: 'primary', label: 'Published' },
  archived:  { Icon: Archive,      variant: 'muted',   label: 'Archived' },
  rejected:  { Icon: XCircle,      variant: 'error',   label: 'Rejected' },
  pending:   { Icon: Clock,        variant: 'warning', label: 'Pending' },
  completed: { Icon: Check,        variant: 'success', label: 'Completed' },
};

function resolveStatus(status: StatusKey): StatusConfig {
  const key = status.toLowerCase();
  return (
    STATUS_MAP[key] ?? {
      Icon: Info,
      variant: 'default',
      label: status.charAt(0).toUpperCase() + status.slice(1).toLowerCase(),
    }
  );
}

// ─── StatusIcon ───────────────────────────────────────────────────────────────

interface StatusIconProps {
  status: StatusKey;
  size?: AppIconSize;
  className?: string;
}

/**
 * Renders the canonical icon for a given entity status.
 *
 * @example
 * <StatusIcon status="active" />
 * <StatusIcon status="PENDING" size="compact" />
 */
export function StatusIcon({ status, size = 'default', className }: StatusIconProps) {
  const { Icon, variant } = resolveStatus(status);
  return <AppIcon Icon={Icon} variant={variant} size={size} className={className} />;
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────

interface StatusBadgeProps {
  status: StatusKey;
  size?: AppIconSize;
  className?: string;
  /** Show icon alongside label */
  showIcon?: boolean;
}

const BADGE_VARIANT_CLASSES: Record<AppIconVariant, string> = {
  default: 'bg-muted text-muted-foreground border-border',
  muted:   'bg-muted text-muted-foreground border-border',
  primary: 'bg-primary-soft text-primary border-primary/20',
  success: 'bg-success-soft text-success border-success/20',
  warning: 'bg-warning-soft text-warning border-warning/20',
  error:   'bg-destructive-soft text-destructive border-destructive/20',
  info:    'bg-info-soft text-info border-info/20',
  inherit: 'bg-muted text-foreground border-border',
};

/**
 * Renders a status badge with optional icon.
 * Sentence case is enforced automatically (per badge standard).
 *
 * @example
 * <StatusBadge status="active" showIcon />
 * <StatusBadge status="DRAFT" />
 */
export function StatusBadge({ status, size = 'default', className, showIcon = false }: StatusBadgeProps) {
  const { Icon, variant, label } = resolveStatus(status);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border app-small-meta font-medium',
        BADGE_VARIANT_CLASSES[variant],
        className
      )}
    >
      {showIcon && (
        <Icon className={cn(SIZE_CLASSES[size], VARIANT_CLASSES[variant])} />
      )}
      {label}
    </span>
  );
}

// ─── Named icon exports (convenience) ────────────────────────────────────────
// Use these in feature modules to avoid importing lucide-react directly.

export const Icons = {
  // Actions
  Eye,
  Pencil,
  Trash2,
  MoreHorizontal,
  Plus,
  Search,
  Filter,
  Download,
  Upload,
  RefreshCw,
  Copy,
  ExternalLink,
  // Navigation
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  X,
  LogOut,
  Home,
  // Status
  CheckCircle,
  CircleSlash,
  PauseCircle,
  FileEdit,
  BadgeCheck,
  Archive,
  XCircle,
  Clock,
  Check,
  // Feedback
  Info,
  AlertTriangle,
  // Domain
  Settings,
  User,
  Users,
  Building2,
  FileText,
  Workflow,
  Bell,
  Shield,
  BarChart2,
  Lock,
  Unlock,
} as const;

export type IconName = keyof typeof Icons;
